import React, { useCallback, useMemo } from 'react';
import DownloadIcon from '@splashthat/doppler/icons/Download';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import PropTypes from 'prop-types';
import { bulkDownloadCalendarAttachments } from 'common/utils/bulkDownloadCalendarAttachments';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';

import { ButtonWithPreview } from 'common/components/TouchpointEditor/components/TouchpointNodes/Button';
import { BRAND_LIBRARY_BUTTON_TEXT, BRAND_LIBRARY_DARK_TEXT_COLOR } from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';

import { StyledAddToCalendarButton, StyledCalendarDownloadTabs } from './AddToCalendarButton.styles';

export function AddToCalendarButton(props) {
    const { fontFamily, fontColor, fontColorHover, fontSize } = props.styles;
    const {
        theme: { activeFonts, colors },
    } = useTouchpointData();
    const { guestAccessHash, eventId, meta } = props;
    const node = useNode();
    const editor = useEditor((state) => ({ editing: state.editing }));
    const fontStyles = useMemo(
        () => ({
            $fontFamily: activeFonts[fontFamily],
            $fontColor: colors[fontColor],
            $fontColorHover: colors[fontColorHover],
            $fontSize: fontSize,
        }),
        [fontFamily, fontColor, fontColorHover, fontSize],
    );
    const { registeredSessions } = useSessions();

    const setContent = useCallback(
        (text) =>
            node.actions.setProp((editedNode) => {
                editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );

    if (!meta.visible || !registeredSessions.length) {
        return null;
    }
    const textEditionDisabled = !editor.editing || (editor.editing && !props.selected && !props.hovered);

    return (
        <StyledCalendarDownloadTabs value="mySessions">
            <StyledAddToCalendarButton
                {...props}
                {...fontStyles}
                onClick={node.actions.onClick(() => bulkDownloadCalendarAttachments(eventId, guestAccessHash))}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                data-qa={props.dataQa}
            >
                <DownloadIcon />
                <TextEditable setContent={setContent} disabled={textEditionDisabled}>
                    {meta.content}
                </TextEditable>
            </StyledAddToCalendarButton>
        </StyledCalendarDownloadTabs>
    );
}

AddToCalendarButton.splash = {
    ...ButtonWithPreview.splash,
    related: {
        settings: React.lazy(() => import('./AddToCalendarButton.tools')),
    },
    defaultProps: {
        styles: {
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontColorHover: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontSize: 16,
            fontFamily: BRAND_LIBRARY_BUTTON_TEXT,
        },
        meta: {
            content: 'Download Confirmed Calendar Attachments',
            visible: true,
        },
    },
};

AddToCalendarButton.propTypes = {
    styles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }),
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    meta: PropTypes.object.isRequired,
    shouldRender: PropTypes.bool,
    dataQa: PropTypes.string,
};

AddToCalendarButton.defaultProps = {
    styles: AddToCalendarButton.splash.defaultProps.styles,
    shouldRender: false,
    dataQa: 'add-sessions-to-calendar-button',
};
